import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';

import { Grid } from '@/components/core/Grid';
import useRealTimeMessaging from '@/hooks/useRealtimeMessaging';
import { useTranslation } from '@/hooks/useTranslation';
import { RealTimeMessagingEvents } from '@/types/RealTimeMessagingEvents';

import { useCommunicationChannelData } from '../communicationChannel/communicationChannel.queries';
import { useAllContactData } from '../contact/contact.queries';
import { useJobData } from './executed.publication.queries';
import { JobDataType, JobEventDataType } from './executed.publication.types';
import {
    DestinationSource,
    enrichScheduledJob,
    mergeScheduledJobUpdate,
} from './scheduled.publication';
import { useScheduledJobColumns } from './scheduled.publication.columns';

export type ScheduledJobsGridViewProps = {
    jobs: JobDataType[];
    columns: ColumnDef<JobDataType>[];
    isLoading?: boolean;
};

const JOB_ROW_HEIGHT = 34;
const JOB_GRID_HEADER_HEIGHT = 32;
const JOB_GRID_EMPTY_HEIGHT = JOB_GRID_HEADER_HEIGHT + JOB_ROW_HEIGHT;
const JOB_GRID_MAX_HEIGHT = 200;

const scheduledJobsGridHeight = (jobCount: number, isLoading?: boolean) => {
    if (isLoading || jobCount === 0) {
        return JOB_GRID_EMPTY_HEIGHT;
    }

    return Math.min(
        jobCount * JOB_ROW_HEIGHT + JOB_GRID_HEADER_HEIGHT,
        JOB_GRID_MAX_HEIGHT,
    );
};

const ScheduledJobsGridView = ({
    jobs,
    columns,
    isLoading = false,
}: ScheduledJobsGridViewProps) => {
    const { t } = useTranslation();
    const height = scheduledJobsGridHeight(jobs.length, isLoading);

    return (
        <div data-testid="scheduled-jobs-grid" className="tw-flex tw-flex-col tw-mb-4">
            <div className="tw-flex tw-justify-between tw-items-center tw-mb-2">
                <p className="tw-font-bold tw-m-0">
                    {t('PUBLICATIONS.SCHEDULED')}
                </p>
            </div>
            <Grid
                className="tw-w-full tw-table-auto tw-break-words"
                style={{ height, maxHeight: JOB_GRID_MAX_HEIGHT }}
                columns={columns}
                thClassName="tw-whitespace-nowrap"
                isWrapContent={true}
                burgerMenu={true}
                isLoading={isLoading}
                storedStateKey="ScheduledJobsV2"
                data={jobs}
            />
        </div>
    );
};

type JobQueryPage = {
    data: JobDataType[];
    cursor?: string;
};

const ScheduledJobsGrid = () => {
    const queryClient = useQueryClient();
    const {
        data: jobs,
        isLoading: isJobsLoading,
        isSuccess: isJobsSuccess,
    } = useJobData({
        variables: { type: 'PUBLICATION' },
        select: (data) => data.pages.flatMap((page) => page.data),
    });

    const { data: channels, isLoading: isChannelsLoading, isSuccess: isChannelsSuccess } =
        useCommunicationChannelData(true);
    const { data: contacts, isLoading: isContactsLoading, isSuccess: isContactsSuccess } =
        useAllContactData(true);

    const onJobChanged = useCallback(
        (eventData: JobEventDataType) => {
            void queryClient.cancelQueries({ queryKey: ['publication/jobs'] });
            queryClient.setQueriesData(
                { queryKey: ['publication/jobs'] },
                (state: InfiniteData<JobQueryPage> | undefined) => {
                    if (!state) {
                        return state;
                    }

                    return {
                        ...state,
                        pages: state.pages.map((page) => ({
                            ...page,
                            data: mergeScheduledJobUpdate(page.data, eventData),
                        })),
                    };
                },
            );
        },
        [queryClient],
    );

    useRealTimeMessaging({
        eventName: RealTimeMessagingEvents.PUBLICATION_JOB_CHANGED_EVENT,
        eventHandler: onJobChanged,
    });

    const destinationSources = useMemo<DestinationSource[]>(() => {
        if (!channels && !contacts) {
            return [];
        }

        return [
            ...((channels as DestinationSource[] | undefined) ?? []),
            ...((contacts as DestinationSource[] | undefined) ?? []),
        ];
    }, [channels, contacts]);

    const columns = useScheduledJobColumns({ channels });

    const isLoading = isJobsLoading || isChannelsLoading || isContactsLoading;
    const jobsData =
        !isLoading && isJobsSuccess && (isChannelsSuccess || isContactsSuccess)
            ? (jobs ?? []).map((job) => enrichScheduledJob(job, destinationSources))
            : jobs ?? [];

    return (
        <ScheduledJobsGridView
            jobs={jobsData}
            columns={columns}
            isLoading={isLoading}
        />
    );
};

ScheduledJobsGrid.displayName = 'ScheduledJobsGrid';
ScheduledJobsGridView.displayName = 'ScheduledJobsGridView';

export { ScheduledJobsGrid, ScheduledJobsGridView };
